import { createPredicate } from "./condition.js";
const SCRIPT_SRC = "https://c.amazon-adsystem.com/aat/amzn.js";
const SCRIPT_ID = "amzn-pixel";
const PROVIDER = "AmazonAds";
let debug = false;
let initialized = false;
const log = (...args) => debug && // eslint-disable-next-line no-console
console.info(...["[amazonAds]"].concat(args));
const isLoaded = () => typeof window !== "undefined" && !!window.amzn;
const loadScript = () => {
  return new Promise((resolve, reject) => {
    if (isLoaded()) {
      log("Amazon Ads script already loaded");
      resolve();
      return;
    }
    if (typeof window === "undefined") {
      reject(new Error("Cannot load Amazon Ads script in non-browser environment"));
      return;
    }
    if (document.getElementById(SCRIPT_ID)) {
      log("Amazon Ads script tag already exists");
      resolve();
      return;
    }
    log("Loading Amazon Ads script...");
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.id = SCRIPT_ID;
    script.async = true;
    script.onload = () => {
      log("Amazon Ads script loaded successfully");
      resolve();
    };
    script.onerror = (error) => {
      log("Failed to load Amazon Ads script", error);
      reject(new Error("Failed to load Amazon Ads script"));
    };
    const firstScript = document.getElementsByTagName("script")[0];
    (firstScript == null ? void 0 : firstScript.parentNode) ? firstScript.parentNode.insertBefore(script, firstScript) : document.head.appendChild(script);
  });
};
const initializeAmazonAds = async (brandId, pixel) => {
  if (!pixel.region || !pixel.amazonTagId) {
    log("Amazon Ads config is missing required fields", pixel);
    return;
  }
  if (initialized) {
    log("Amazon Ads already initialized");
    return;
  }
  try {
    await loadScript();
    if (!window.amzn) {
      log("Amazon Ads script loaded but amzn object not available");
      return;
    }
    window.amzn("setRegion", pixel.region);
    window.amzn("addTag", pixel.amazonTagId);
    initialized = true;
    log("Amazon Ads initialized with config", { brandId, region: pixel.region, amazonTagId: pixel.amazonTagId });
  } catch (error) {
    log("Failed to initialize Amazon Ads", error);
  }
};
const trackAmazonEvent = (eventName, params) => {
  if (!isLoaded()) {
    log("Cannot track event - Amazon Ads not initialized");
    return;
  }
  try {
    if (params) {
      window.amzn("trackEvent", eventName, params);
    } else {
      window.amzn("trackEvent", eventName);
    }
    log(`Tracked ${eventName} event`, params);
  } catch (error) {
    log(`Failed to track ${eventName} event`, error);
  }
};
const init = (config) => {
  return initializeAmazonAds(config.brandId, {
    pixelProvider: PROVIDER,
    region: config.region,
    amazonTagId: config.amazonTagId
  });
};
const amazonAds = {
  enableDebug: () => {
    debug = true;
  },
  track({
    brandId,
    pixels,
    eventName,
    eventType,
    eventPayload
  }) {
    pixels.forEach(async (pixel) => {
      if (pixel.eventFilter) {
        const predicate = createPredicate(pixel.eventFilter);
        if (!predicate(eventPayload)) {
          log("eventFilter not matched", pixel.eventFilter, pixel.pixelId, eventName, eventPayload);
          return;
        }
      }
      await initializeAmazonAds(brandId, pixel);
      if (!initialized) return;
      switch (eventType) {
        default:
          trackAmazonEvent(eventName, eventPayload);
          break;
      }
    });
  },
  grantConsent() {
    if (!isLoaded()) {
      log("Cannot grant consent - Amazon Ads not initialized");
      return;
    }
    log("Amazon Ads consent granted (managed at tag level)");
  },
  revokeConsent() {
    if (!isLoaded()) {
      log("Cannot revoke consent - Amazon Ads not initialized");
      return;
    }
    log("Amazon Ads consent revoked (managed at tag level)");
  },
  // For backward compatibility
  init,
  isLoaded
};
export {
  amazonAds as default
};
//# sourceMappingURL=amazonAds.js.map
